import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowStepLoop", "Heading", "id", "imageHeight", "MainContent", "maxWidth", "onStepIndexChange", "stepIndex", "steps", "titleText", "use", "cssStyles"];
import unescapedText from 'I18n/utils/unescapedText';
import { Component } from 'react';
import partial from 'react-utils/partial';
import { ControllableNext } from '../Controllable/ControllableNext';
import { rgba } from '../../utils/color';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { NextButton } from '../../components/NextButton/NextButton';
import { PreviousButton } from '../../components/PreviousButton/PreviousButton';
import { ButtonWrapper } from '../../components/ButtonWrapper/ButtonWrapper';
import { Clickable } from '../../components/Clickable/Clickable';
import styled from 'foundations-theming/utils';
import { toPx } from '../../../utils/toPx';
import { uniqueId } from '../../utils/uniqueId';
import { Text } from '../../../components/Text';
import { Image as NewImage } from '../../../components/Image/Image';
import { withTokenProps } from '../../../utils/withTokenProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const preloadImages = steps => {
  if (steps) {
    steps.forEach(step => {
      if (step && step.image) {
        const img = new Image();
        img.src = step.image;
      }
    });
  }
};
const DEFAULT_MAX_WIDTH = 826; // width + gutters
const GUTTER_SIZE = 20;
const MAX_THUMBNAILS = 5;
const THUMBNAIL_ASPECT_RATIO = 0.6909090909; /* based on 76/110 spec */
const THUMBNAIL_GAP = 24;
const FLEX_FALLBACK_SPACE = parseInt('32px', 10) + GUTTER_SIZE;
const STACKED_LAYOUT = `
  'header header    header  '
  '.      steps     .       '
  'prev   contents  next    '
  '.      callout   .       '
  '.      footer    .       '
`;
const RESPONSIVE_LAYOUT = `
  'header     header      header  '
  'steps      steps       steps   '
  'contents   contents    contents'
  'prev       footer      next    '
  'callout    callout     callout '
`;
const CarouselWrapper = styled.section.withConfig({
  displayName: "Carousel__CarouselWrapper",
  componentId: "sc-4w8l51-0"
})(["flex-wrap:wrap;grid-column-gap:", ";grid-template-areas:", ";grid-template-columns:auto 1fr auto;max-width:", ";padding:", ";padding-top:0;position:relative;> *{flex:1 1 100%;}display:grid;@media only screen and (max-width:544px){grid-template-areas:", ";grid-template-columns:auto 1fr auto;&& > *{margin-right:0;}}", ""], toPx(GUTTER_SIZE), STACKED_LAYOUT, ({
  maxWidth
}) => toPx(maxWidth), toPx(GUTTER_SIZE), RESPONSIVE_LAYOUT, ({
  $cssStyles
}) => $cssStyles && Object.assign({}, $cssStyles));
const CarouselHeader = styled.div.withConfig({
  displayName: "Carousel__CarouselHeader",
  componentId: "sc-4w8l51-1"
})(({
  theme
}) => {
  return {
    gridArea: 'header',
    marginBottom: theme.space['600'],
    textAlign: 'center',
    marginRight: theme.space['0']
  };
});
const CarouselCallout = styled.aside.withConfig({
  displayName: "Carousel__CarouselCallout",
  componentId: "sc-4w8l51-2"
})(({
  theme
}) => {
  return {
    gridArea: 'callout',
    marginTop: theme.space['500'],
    textAlign: 'center',
    marginLeft: theme.space['0'],
    marginRight: theme.space['0'],
    '@media only screen and (max-width: 544px)': {
      marginTop: theme.space['500'],
      paddingLeft: theme.space['0']
    }
  };
});
const CarouselContent = styled.section.withConfig({
  displayName: "Carousel__CarouselContent",
  componentId: "sc-4w8l51-3"
})(["flex-basis:calc(100% - ", ");grid-area:contents;align-self:center;"], toPx(FLEX_FALLBACK_SPACE * 2));
const CarouselContentInner = styled.div.withConfig({
  displayName: "Carousel__CarouselContentInner",
  componentId: "sc-4w8l51-4"
})([">:last-child{margin-bottom:0;}"]);
const CarouselNext = styled.div.withConfig({
  displayName: "Carousel__CarouselNext",
  componentId: "sc-4w8l51-5"
})(["align-self:center;flex-basis:", ";grid-area:next;text-align:right;"], toPx(FLEX_FALLBACK_SPACE));
const CarouselPrevious = styled.div.withConfig({
  displayName: "Carousel__CarouselPrevious",
  componentId: "sc-4w8l51-6"
})(["align-self:center;flex-basis:", ";grid-area:prev;"], toPx(FLEX_FALLBACK_SPACE));
const CarouselFooter = styled.footer.withConfig({
  displayName: "Carousel__CarouselFooter",
  componentId: "sc-4w8l51-7"
})(({
  theme
}) => {
  return {
    gridArea: 'footer',
    marginTop: theme.space['500'],
    marginRight: theme.space['0'],
    padding: theme.space['0']
  };
});
const CarouselThumbnail = styled(Clickable)(({
  theme,
  'aria-current': ariaCurrent
}) => {
  return {
    flex: '0 1',
    flexBasis: `calc(20% - ${toPx(THUMBNAIL_GAP)})`,
    borderRadius: theme.borderRadius['100'],
    boxShadow: ariaCurrent ? `
          0 0 0 2px #00A4BD,
          0 0 12px 0 ${rgba('00A4BD', 0.5)}
        ` : `0 0 0 1px #7FD1DE`
  };
});
const AspectRatioBox = styled.div.withConfig({
  displayName: "Carousel__AspectRatioBox",
  componentId: "sc-4w8l51-8"
})(({
  theme
}) => {
  return {
    height: theme.space['0'],
    overflow: 'hidden',
    paddingTop: `calc(100% * ${THUMBNAIL_ASPECT_RATIO})`,
    position: 'relative'
  };
});
const CarouselThumbnailImageWrapper = styled.div.withConfig({
  displayName: "Carousel__CarouselThumbnailImageWrapper",
  componentId: "sc-4w8l51-9"
})(({
  theme
}) => {
  return {
    height: theme.space['0'],
    left: '50%',
    minHeight: '100%',
    minWidth: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    width: 'auto'
  };
});
const isValidStepIndex = (stepIndex, steps) => {
  if (!stepIndex || !steps) {
    return false;
  }
  return !(stepIndex < 0 || stepIndex > steps.length - 1);
};
const getMaxIndex = arr => arr.length - 1;
class CarouselComponent extends Component {
  constructor(...args) {
    super(...args);
    this.handleClickPreviousStep = () => {
      const {
        allowStepLoop,
        stepIndex,
        steps
      } = this.props;
      const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      let newIndex;
      if (typeof computedStepIndex === 'number') {
        if (allowStepLoop && computedStepIndex === 0) {
          newIndex = getMaxIndex(steps);
        } else {
          newIndex = computedStepIndex - 1;
        }
        if (newIndex >= 0) {
          this.handleChange(newIndex);
        }
      }
    };
    this.handleClickNextStep = () => {
      const {
        allowStepLoop,
        stepIndex,
        steps
      } = this.props;
      const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      let newIndex;
      if (typeof computedStepIndex === 'number') {
        if (allowStepLoop && getMaxIndex(steps) === computedStepIndex) {
          newIndex = 0;
        } else {
          newIndex = computedStepIndex + 1;
        }
        if (newIndex <= getMaxIndex(steps)) {
          this.handleChange(newIndex);
        }
      }
    };
    this.handleChange = newIndex => {
      if (this.props.onStepIndexChange) {
        this.props.onStepIndexChange(syntheticEvent(newIndex));
      }
    };
    this.handleKeyDown = evt => {
      if (evt.key === 'ArrowRight') {
        this.handleClickNextStep();
      }
      if (evt.key === 'ArrowLeft') {
        this.handleClickPreviousStep();
      }
    };
  }
  componentDidMount() {
    const {
      steps
    } = this.props;
    preloadImages(steps);
  }
  componentDidUpdate() {
    const {
      steps
    } = this.props;
    preloadImages(steps);
  }
  renderCount(steps, computedStepIndex) {
    const totalSteps = steps ? steps.length : 0;
    const currentStepDisplayNumber = computedStepIndex + 1;

    // TODO this component is currently relying on the sr-only utility class which feels wrong at this stage
    const className = 'sr-only';
    return /*#__PURE__*/_jsxs(Text, {
      typography: "body-100",
      display: "block",
      marginBlock: "0",
      marginInline: "200",
      textAlign: "center",
      children: [/*#__PURE__*/_jsx("span", {
        className: className,
        children: unescapedText('ui.UICarousel.current', {
          currentStepDisplayNumber,
          totalSteps
        })
      }), /*#__PURE__*/_jsx("span", {
        "aria-hidden": true,
        children: `${currentStepDisplayNumber}/${totalSteps}`
      })]
    });
  }
  renderFooter(allowStepLoop, use, steps, computedStepIndex) {
    const totalSteps = steps ? steps.length : 0;
    const renderedCount = allowStepLoop ? null : this.renderCount(steps, computedStepIndex);
    let renderedFooter;
    renderedFooter = renderedCount;
    if (use === 'max') {
      renderedFooter = totalSteps > MAX_THUMBNAILS ? renderedCount : this.renderThumbnails(steps, computedStepIndex);
    }
    return renderedFooter && /*#__PURE__*/_jsx(CarouselFooter, {
      use: use,
      children: renderedFooter
    });
  }
  renderThumbnails(steps, computedStepIndex) {
    const totalThumbs = steps;
    const thumbs = totalThumbs ? totalThumbs.map((step, i) => {
      if (!step) return null;
      if (!step.image) return null;
      const isCurrentStep = computedStepIndex === i || undefined;
      return /*#__PURE__*/_jsx(CarouselThumbnail, {
        "aria-current": isCurrentStep,
        onClick: partial(this.handleChange, i),
        children: /*#__PURE__*/_jsx(AspectRatioBox, {
          children: /*#__PURE__*/_jsx(CarouselThumbnailImageWrapper, {
            children: /*#__PURE__*/_jsx(NewImage, {
              alt: step.alt,
              src: step.image,
              maxInlineSize: "100%",
              minBlockSize: "100%"
            })
          })
        })
      }, i);
    }) : null;
    return /*#__PURE__*/_jsx(ButtonWrapper, {
      alignItems: "start",
      horizontalGap: THUMBNAIL_GAP,
      children: thumbs
    });
  }
  render() {
    const _this$props = this.props,
      {
        allowStepLoop = false,
        Heading = Text,
        id,
        imageHeight,
        MainContent = CarouselContentInner,
        maxWidth = DEFAULT_MAX_WIDTH,
        stepIndex = 0,
        steps,
        titleText,
        use = 'lite',
        cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
    this._id = this._id || uniqueId('carousel');
    const computedId = id || this._id;
    const describedById = `${computedId}-desc`;
    const currentStep = steps ? steps[computedStepIndex] : null;
    const isFirstStep = computedStepIndex === 0;
    const isLastStep = computedStepIndex === getMaxIndex(steps);
    return /*#__PURE__*/_jsxs(CarouselWrapper, Object.assign({}, rest, {
      $cssStyles: cssStyles,
      "aria-atomic": true,
      "aria-describedby": describedById,
      "aria-live": "polite",
      id: computedId,
      onKeyDown: this.handleKeyDown,
      maxWidth: maxWidth,
      tabIndex: 0,
      use: use,
      children: [titleText && /*#__PURE__*/_jsx(CarouselHeader, {
        use: use,
        children: /*#__PURE__*/_jsx(Heading, {
          typography: "heading-300",
          marginBlockEnd: "0",
          children: titleText
        })
      }), /*#__PURE__*/_jsx("div", {
        "aria-hidden": true,
        className: "sr-only",
        id: describedById,
        children: unescapedText('ui.UICarousel.tip')
      }), /*#__PURE__*/_jsx(CarouselPrevious, {
        children: /*#__PURE__*/_jsx(PreviousButton, {
          "aria-controls": computedId,
          "aria-hidden": true,
          "aria-label": unescapedText('ui.UICarousel.prev'),
          onClick: this.handleClickPreviousStep,
          disabled: isFirstStep && !allowStepLoop,
          size: "small"
        })
      }), /*#__PURE__*/_jsx(CarouselContent, {
        use: use,
        children: currentStep && /*#__PURE__*/_jsx(MainContent, {
          children: currentStep.image ? /*#__PURE__*/_jsx(NewImage, {
            alt: currentStep.alt,
            objectFit: "contain",
            maxInlineSize: "100%",
            display: "block",
            maxBlockSize: imageHeight ? toPx(imageHeight) : undefined,
            borderRadius: "100",
            src: currentStep.image
          }) : currentStep.content
        })
      }), /*#__PURE__*/_jsx(CarouselNext, {
        children: /*#__PURE__*/_jsx(NextButton, {
          "aria-controls": computedId,
          "aria-hidden": true,
          "aria-label": unescapedText('ui.UICarousel.next'),
          onClick: this.handleClickNextStep,
          disabled: isLastStep && !allowStepLoop,
          size: "small"
        })
      }), currentStep && currentStep.description && /*#__PURE__*/_jsxs(CarouselCallout, {
        use: use,
        children: [currentStep.name && /*#__PURE__*/_jsx(Text, {
          role: "presentation",
          typography: "heading-50",
          marginBlockEnd: "300",
          children: currentStep.name
        }), currentStep.description]
      }), this.renderFooter(allowStepLoop, use, steps, computedStepIndex)]
    }));
  }
}
export const Carousel = ControllableNext(withTokenProps(CarouselComponent), ['stepIndex']);